// src/pages/admin/AdminProducts.jsx
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  getProducts, addProduct, updateProduct, deleteProduct, getCategories,
} from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiChevronLeft, FiChevronRight, FiBox } from "react-icons/fi";

export default function AdminProducts() {
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [images,     setImages]     = useState([]);
  const [saving,     setSaving]     = useState(false);
  const [search,     setSearch]     = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const { register, handleSubmit, reset, setValue, control, formState: { errors } } = useForm();

  // Watch unit + the relevant quantity/price fields to auto-calculate price
  const unit          = useWatch({ control, name: "unit" });
  const weightSize    = useWatch({ control, name: "weightSize" });
  const pricePerKg    = useWatch({ control, name: "pricePerKg" });
  const packSize      = useWatch({ control, name: "packSize" });
  const pricePerPack  = useWatch({ control, name: "pricePerPack" });

  const isKg   = unit === "kg";
  const isPack = unit === "pack";

  useEffect(() => {
    if (isKg) {
      const w = parseFloat(weightSize);
      const p = parseFloat(pricePerKg);
      if (!isNaN(w) && !isNaN(p) && w > 0 && p > 0) {
        setValue("price", (w * p).toFixed(2));
      }
    } else if (isPack) {
      const s = parseFloat(packSize);
      const p = parseFloat(pricePerPack);
      if (!isNaN(s) && !isNaN(p) && s > 0 && p > 0) {
        setValue("price", (s * p).toFixed(2));
      }
    }
  }, [isKg, isPack, weightSize, pricePerKg, packSize, pricePerPack, setValue]);

  // Whether the auto-calc inputs are complete enough to have produced a price
  const priceIsAutoCalculated =
    (isKg && weightSize && pricePerKg) || (isPack && packSize && pricePerPack);

  const load = async () => {
    const [p, c] = await Promise.all([getProducts(), getCategories()]);
    setProducts(p);
    setCategories(c);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const openAdd = () => {
    setEditing(null);
    setImages([]);
    reset({
      name: "", price: "", originalPrice: "", unit: "kg",
      description: "", categoryId: "", inStock: true, featured: false,
      weightSize: "", pricePerKg: "", packSize: "", pricePerPack: "",
      hsnSac: "",
    });
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setImages(product.images || []);
    reset({
      name:          product.name,
      price:         product.price,
      originalPrice: product.originalPrice || "",
      unit:          product.unit || "kg",
      description:   product.description || "",
      categoryId:    product.categoryId || "",
      inStock:       product.inStock !== false,
      featured:      product.featured || false,
      weightSize:    product.weightSize || "",
      pricePerKg:    product.pricePerKg || "",
      packSize:      product.packSize || "",
      pricePerPack:  product.pricePerPack || "",
      hsnSac:        product.hsnSac || "",
    });
    setModalOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    const category = categories.find((c) => c.id === data.categoryId)?.name || "";
    const payload = {
      ...data,
      price:         parseFloat(data.price),
      originalPrice: data.originalPrice ? parseFloat(data.originalPrice) : null,
      weightSize:    data.unit === "kg"   && data.weightSize   ? parseFloat(data.weightSize)   : null,
      pricePerKg:    data.unit === "kg"   && data.pricePerKg   ? parseFloat(data.pricePerKg)   : null,
      packSize:      data.unit === "pack" && data.packSize     ? parseFloat(data.packSize)     : null,
      pricePerPack:  data.unit === "pack" && data.pricePerPack ? parseFloat(data.pricePerPack) : null,
      hsnSac:        data.hsnSac ? data.hsnSac.trim() : "",
      // Drop any empty/undefined slots the image uploader may leave behind
      // (e.g. mid-upload placeholders) — Firestore rejects undefined entries.
      images:        (images || []).filter((img) => typeof img === "string" && img.trim() !== ""),
      category,
      inStock:  data.inStock  === true || data.inStock  === "true",
      featured: data.featured === true || data.featured === "true",
    };

    // Final safety net: Firestore's updateDoc()/setDoc() reject any field
    // whose value is undefined, so strip those out before sending.
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) delete payload[key];
    });

    try {
      if (editing) {
        await updateProduct(editing.id, payload);
        toast.success("Product updated");
      } else {
        await addProduct(payload);
        toast.success("Product added");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await deleteProduct(id);
    toast.success("Product deleted");
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const filtered = products.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  // Compact page-number list with ellipses, e.g. 1 … 4 5 [6] 7 8 … 12
  const getPageNumbers = () => {
    const pages = [];
    const windowSize = 1;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= safePage - windowSize && i <= safePage + windowSize)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "…") {
        pages.push("…");
      }
    }
    return pages;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm sm:text-md flex items-center gap-2">
          <FiPlus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-sm w-full sm:w-auto">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="input pl-9 py-2 text-sm w-64"
          />
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 text-primary-700 text-sm font-semibold border border-primary-100">
          <FiBox className="w-4 h-4" /> {products.length} total
        </span>
      </div>

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden border border-gray-200 rounded-xl shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Image", "Name", "Category", "HSN/SAC", "Weight/Pack", "Price", "Rate", "Unit", "Stock", "Featured", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-16">
                      <div className="flex flex-col items-center justify-center text-gray-400 gap-2">
                        <FiBox className="w-8 h-8" />
                        <p className="text-sm font-medium text-gray-500">No products found</p>
                        <p className="text-xs text-gray-400">Try adjusting your search</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginated.map((p, idx) => (
                    <tr key={p.id} className={`transition-colors hover:bg-primary-50/40 ${idx % 2 === 1 ? "bg-gray-50/50" : "bg-white"}`}>
                      <td className="px-4 py-3.5">
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100">
                          {p.images?.[0]
                            ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center text-lg">🌱</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 font-medium text-gray-900 max-w-xs">
                        <p className="truncate">{p.name}</p>
                        {p.description && <p className="text-xs text-gray-400 truncate">{p.description}</p>}
                      </td>
                      <td className="px-4 py-3.5 text-gray-600">{p.category || "—"}</td>
                      <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">{p.hsnSac || "—"}</td>
                      <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">
                        {p.unit === "pack"
                          ? (p.packSize ? `${p.packSize} pack(s)` : "—")
                          : (p.weightSize ? `${p.weightSize} kg` : "—")}
                      </td>
                      <td className="px-4 py-3.5 font-semibold text-primary-600 whitespace-nowrap">
                        ₹{p.price}
                        {p.originalPrice && (
                          <span className="text-xs text-gray-400 line-through ml-1">₹{p.originalPrice}</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap">
                        {p.unit === "pack"
                          ? (p.pricePerPack ? `₹${p.pricePerPack}/pack` : "—")
                          : (p.pricePerKg ? `₹${p.pricePerKg}/kg` : "—")}
                      </td>
                      <td className="px-4 py-3.5 text-gray-600">{p.unit || "—"}</td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-block text-xs font-medium px-2 py-1 rounded-md border ${p.inStock !== false ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                          {p.inStock !== false ? "In Stock" : "Out"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {p.featured
                          ? <span className="inline-block text-xs font-medium px-2 py-1 rounded-md bg-amber-100 text-amber-700 border border-amber-200">⭐ Yes</span>
                          : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(p)} title="Edit product" className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors">
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(p.id, p.name)} title="Delete product" className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination footer ── */}
          {filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50/50">
              <p className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-700">{startIdx + 1}</span>–
                <span className="font-medium text-gray-700">{Math.min(startIdx + PAGE_SIZE, filtered.length)}</span> of{" "}
                <span className="font-medium text-gray-700">{filtered.length}</span> products
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronLeft className="w-4 h-4" />
                </button>

                {getPageNumbers().map((p, i) =>
                  p === "…" ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`min-w-[2rem] h-8 px-2 rounded-lg text-xs font-medium border transition-colors ${p === safePage ? "bg-primary-600 text-white border-primary-600 shadow-sm" : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"}`}>
                      {p}
                    </button>
                  )
                )}

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-white hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit Product" : "Add Product"}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">

            {/* Product Name */}
            <div className="col-span-2">
              <label className="label">Product Name *</label>
              <input
                {...register("name", { required: "Name is required" })}
                className={`input ${errors.name ? "border-red-400" : ""}`}
                placeholder="e.g. Russian Yellow Pea"
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>}
            </div>

            {/* Category */}
            <div>
              <label className="label">Category</label>
              <select {...register("categoryId")} className="input">
                <option value="">— Select Category —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* HSN/SAC */}
            <div>
              <label className="label">HSN/SAC</label>
              <input
                {...register("hsnSac")}
                className="input"
                placeholder="e.g. 1209"
              />
            </div>

            {/* Unit */}
            <div>
              <label className="label">Unit</label>
              <select {...register("unit")} className="input">
                <option value="kg">kg</option>
                <option value="pack">pack</option>
              </select>
            </div>

            {/* Spacer to keep grid alignment when only one of the two unit-specific blocks below renders */}
            <div className="hidden sm:block" />

            {isKg && (
              <>
                {/* Weight (kg) */}
                <div>
                  <label className="label">Weight (kg)</label>
                  <input
                    {...register("weightSize", {
                      min: { value: 0, message: "Must be positive" },
                    })}
                    type="number"
                    step="0.001"
                    className={`input ${errors.weightSize ? "border-red-400" : ""}`}
                    placeholder="e.g. 30"
                  />
                  {errors.weightSize && <p className="text-xs text-red-500 mt-1">{errors.weightSize.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">Enter the package weight in kg</p>
                </div>

                {/* Price Per KG */}
                <div>
                  <label className="label">Price Per KG (₹)</label>
                  <input
                    {...register("pricePerKg", {
                      min: { value: 0, message: "Must be positive" },
                    })}
                    type="number"
                    step="0.01"
                    className={`input ${errors.pricePerKg ? "border-red-400" : ""}`}
                    placeholder="e.g. 48.50"
                  />
                  {errors.pricePerKg && <p className="text-xs text-red-500 mt-1">{errors.pricePerKg.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">Auto-calculates total price below</p>
                </div>
              </>
            )}

            {isPack && (
              <>
                {/* Pack Size */}
                <div>
                  <label className="label">Pack Size (units)</label>
                  <input
                    {...register("packSize", {
                      min: { value: 0, message: "Must be positive" },
                    })}
                    type="number"
                    step="1"
                    className={`input ${errors.packSize ? "border-red-400" : ""}`}
                    placeholder="e.g. 1"
                  />
                  {errors.packSize && <p className="text-xs text-red-500 mt-1">{errors.packSize.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">Number of packs/units in this item</p>
                </div>

                {/* Price Per Pack */}
                <div>
                  <label className="label">Price Per Pack (₹)</label>
                  <input
                    {...register("pricePerPack", {
                      min: { value: 0, message: "Must be positive" },
                    })}
                    type="number"
                    step="0.01"
                    className={`input ${errors.pricePerPack ? "border-red-400" : ""}`}
                    placeholder="e.g. 99.00"
                  />
                  {errors.pricePerPack && <p className="text-xs text-red-500 mt-1">{errors.pricePerPack.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">Auto-calculates total price below</p>
                </div>
              </>
            )}

            {/* Price — always auto-calculated, read-only */}
            <div>
              <label className="label">
                Price (₹) *
                <span className="ml-2 text-xs font-normal text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                  Auto-calculated
                </span>
              </label>
              <input
                {...register("price", {
                  required: "Price is required",
                  min: { value: 0, message: "Must be positive" },
                })}
                type="number"
                step="0.01"
                readOnly
                className={`input bg-gray-100 text-gray-700 font-semibold cursor-not-allowed ${errors.price ? "border-red-400" : ""}`}
                placeholder="50"
              />
              {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price.message}</p>}
              {priceIsAutoCalculated && (
                <p className="text-xs text-primary-600 mt-1">
                  {isKg
                    ? `= ${weightSize} kg × ₹${pricePerKg}/kg`
                    : `= ${packSize} pack(s) × ₹${pricePerPack}/pack`}
                </p>
              )}
            </div>

            {/* Original Price */}
            <div>
              <label className="label">Original Price (₹)</label>
              <input
                {...register("originalPrice")}
                type="number"
                step="0.01"
                className="input"
                placeholder="1600 (optional, shown as strikethrough)"
              />
            </div>

            {/* Description */}
            <div className="col-span-2">
              <label className="label">Description</label>
              <textarea
                {...register("description")}
                rows={3}
                className="input resize-none"
                placeholder="Product description..."
              />
            </div>

            {/* Checkboxes */}
            <div className="flex items-center gap-3">
              <input
                {...register("inStock")}
                type="checkbox"
                id="inStock"
                className="w-4 h-4 accent-primary-600"
                defaultChecked
              />
              <label htmlFor="inStock" className="text-sm text-gray-700 font-medium">In Stock</label>
            </div>
            <div className="flex items-center gap-3">
              <input
                {...register("featured")}
                type="checkbox"
                id="featured"
                className="w-4 h-4 accent-primary-600"
              />
              <label htmlFor="featured" className="text-sm text-gray-700 font-medium">Featured Product</label>
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="label">Product Images</label>
            {/*
              FIX: `key={editing ? editing.id : "new"}` forces React to fully
              unmount/remount ImageUploader whenever we switch between
              "Add Product" and "Edit Product", or between two different
              products. Without this, ImageUploader's internal `previews`
              state could keep holding a PREVIOUS product's images, so adding
              a new image would append to the wrong (stale) list and
              overwrite the current product's real images on save.
              This is used together with the internal sync-fix inside
              ImageUploader.jsx as a second layer of protection.
            */}
            <ImageUploader
              key={editing ? editing.id : "new"}
              folder="products"
              onUpload={setImages}
              existingImages={images}
              maxFiles={6}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update Product" : "Add Product"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}